import { FunctionComponent, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { faChevronDown, faChevronUp, faClock } from '@fortawesome/pro-regular-svg-icons';
import useNonInitialEffect from '@versiondos/hooks';
import { DaycareServiceWrapper } from 'model/Service';
import styles from './SelectDaycareServices.module.scss';
import ClickAwayListener from 'components/CreateBooking/UI/ClickAwayListener';

export interface SelectDaycareServicesProps {
    id?: string;
    label?: string | JSX.Element;
    name?: string;
    selectedOption?: DaycareServiceWrapper;
    placeholder?: string;
    options: Array<DaycareServiceWrapper>;
    disabled?: boolean;
    className?: string;
    required?: boolean;
    information?: string;
    customizable?: boolean;
    onSelect: (option: DaycareServiceWrapper, data: boolean) => void;
}

const getDuration = (selectedOption?: DaycareServiceWrapper) => {
    if (!selectedOption) return '';

    if (selectedOption.variableDuration) {
        try {
            const parsed = JSON.parse(selectedOption.variableDuration);

            switch (parsed.duration_option) {
                case 'full_day':
                    return 'Full Day';
                case 'half_day':
                    return 'Half Day';
                case 'hourly':
                    return 'Hourly';
                default:
                    break;
            }
        } catch {
            return '';
        }
    }

    const duration = selectedOption.duration;

    if (duration) {
        if (duration > 240) return 'Full Day';
        if (duration > 60 && duration <= 240) return 'Half Day';
        return 'Hourly';
    }

    return '';
};

export const SelectDaycareServices: FunctionComponent<SelectDaycareServicesProps> = props => {
    const [isCollapsed, setIsCollapsed] = useState(true);
    const [defaultState, setDefault] = useState<string | undefined>(undefined);

    const changeHandler = (idSelected: number) => {
        const optionSelected = props.options.find(option => option.serviceId === idSelected)!;
        props.onSelect(optionSelected, false);
        setDefault(optionSelected?.price?.toFixed(2));
    };

    useNonInitialEffect(() => {
        const timer: NodeJS.Timer = setTimeout(() => {
            if (defaultState) {
                setDefault(undefined);
            }
        }, 500);
        return () => clearTimeout(timer);
    }, [defaultState]);

    const header = (
        <div className={styles.headerContainer}>
            <div className={styles.textContainer}>
                <span
                    className={clsx(styles.header, {
                        [styles.placeholder]: !props.selectedOption
                    })}
                >
                    {props.selectedOption?.serviceId && (
                        <div
                            className={styles.color}
                            style={{
                                backgroundColor:
                                    props.options.find(option => option.serviceId === props.selectedOption?.serviceId)
                                        ?.serviceColor ?? 'inherit'
                            }}
                        />
                    )}
                    {props.selectedOption?.serviceName ? props.selectedOption?.serviceName : props.placeholder}
                </span>
            </div>
            <div className={styles.chevronContainer}>
                <FontAwesomeIcon
                    icon={isCollapsed ? faChevronDown : faChevronUp}
                    color={isCollapsed ? '#7A7A7A' : '#222222'}
                    className={styles.chevron}
                />
            </div>
        </div>
    );

    const list = (
        <div className={styles.listContainer}>
            {props.options.map((opt, index) => (
                <div
                    className={styles.itemContainer}
                    key={opt.serviceId}
                    style={{
                        borderColor: opt?.serviceColor ?? '#ffffff'
                    }}
                    id={`booking_option_service_${index}`}
                    onClick={() => changeHandler(opt.serviceId)}
                >
                    <div className={styles.itemContent}>
                        <div className={clsx(styles.serviceItemContainer, styles.serviceNameContainer)}>
                            <span className={clsx(styles.item, styles.sectionService)}>{opt.serviceName}</span>
                        </div>
                        <div className={styles.sectionDuration}>
                            <div className={styles.serviceItemContainer}>
                                <div className={styles.durationContainer}>
                                    <FontAwesomeIcon
                                        icon={faClock}
                                        className={clsx(styles.item)}
                                    />
                                    <span className={clsx(styles.item, styles.spacing)}>{getDuration(opt)}</span>
                                </div>
                            </div>
                            <div className={styles.serviceItemContainer}>
                                <span className={clsx(styles.item, styles.bold, styles.spacing)}>
                                    ${opt.price?.toFixed(2)}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    const collapsed = (
        <div
            className={clsx(styles.border, styles.collapsedContainer, [styles.optionNotSelected])}
            onClick={() => setIsCollapsed(false)}
        >
            {header}
        </div>
    );

    const expanded = (
        <div
            className={clsx(styles.border, styles.expandedContainer)}
            onClick={() => setIsCollapsed(true)}
        >
            {header}
            {list}
        </div>
    );

    return (
        <ClickAwayListener onClickAway={() => setIsCollapsed(true)}>
            <div className={clsx(styles.container, props.className)}>
                <div
                    className={styles.dropdownContainer}
                    id="booking_list_services"
                >
                    {isCollapsed ? collapsed : expanded}
                </div>
                {props.customizable && props.selectedOption && <div className={styles.customData}></div>}
            </div>
        </ClickAwayListener>
    );
};

export default SelectDaycareServices;
